import { Component, OnInit, OnDestroy, ChangeDetectorRef } from "@angular/core";
import { CommonModule } from "@angular/common";
import { StoryboardService } from "../services/storyboard.service";
import { SharedService } from "../services/shared.service";
import { ActivatedRoute } from "@angular/router";
import { finalize } from "rxjs";
import { SharedModule } from "@shared/shared.module";
import { url } from "../global/constant";
import { NzModalService } from "ng-zorro-antd/modal";
import { RegenerateStoryboardShotComponent } from "@shared/common-components/regenerate-storyboard-shot/regenerate-storyboard-shot.component";
import { CreditService } from "../services/credit.service";
import { Subscription } from "rxjs";

@Component({
  selector: "app-storyboard",
  standalone: true,
  imports: [CommonModule, SharedModule],
  templateUrl: "./storyboard.component.html",
  styleUrl: "./storyboard.component.scss",
})
export class StoryboardComponent implements OnInit, OnDestroy {
  // Panel state
  isLeftPanelCollapsed: boolean = false;

  // View mode
  viewMode: "grid" | "full" = "grid";

  // Scene selection
  selectedScene: any = null;

  // Active tab
  activeTab: "details" | "script" | "mood" = "details";

  // Credits
  credits: number = 0;
  private creditSubscription?: Subscription;

  // Current shot for full view
  currentShotIndex: number = 0;
  totalDuration: number = 0;

  storyboardId: string | null = null;
  videoId: string | null = null;
  storyboardDetails: any = null;
  selectedFrameDetails: any = null;

  constructor(
    private storyboardService: StoryboardService,
    private sharedService: SharedService,
    private route: ActivatedRoute,
    private modalService: NzModalService,
    private creditService: CreditService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.storyboardId =
      this.route.snapshot.queryParamMap.get("storyboardId") || null;
    this.videoId = this.route.snapshot.queryParamMap.get("videoId") || null;
    this.fetchStoryboardDetails();

    // Subscribe to credit count updates
    this.creditSubscription = this.creditService.$creditCount.subscribe(
      (count) => {
        this.credits = count;
        this.cdr.detectChanges();
      }
    );

    // Note: refreshCredits() is automatically called by CreditService when companyId changes
    // No need to call it manually here
  }

  ngOnDestroy(): void {
    // Unsubscribe from credit updates
    if (this.creditSubscription) {
      this.creditSubscription.unsubscribe();
    }
  }

  fetchStoryboardDetails() {
    this.sharedService.showSpinner();
    this.storyboardService
      .fetchStoryboardDetails(this.storyboardId)
      .pipe(finalize(() => this.sharedService.hideSpinner()))
      .subscribe((res: any) => {
        if (res && res.statusCode === 200) {
          this.storyboardDetails = res.data;
          this.totalDuration = this.storyboardDetails?.shotlist?.scenes?.reduce(
            (acc, scene) => acc + scene.duration_seconds,
            0
          );
          // Auto-select "All Scenes" if no scene is selected
          if (
            !this.selectedScene &&
            this.storyboardDetails?.shotlist?.scenes?.length > 0
          ) {
            this.selectScene("all");
          }
        }
      });
  }

  goBack(): void {
    if (!this.storyboardId) {
      history.back();
    } else {
      this.sharedService.gotopage(
        url.videoDetails +
          `?id=${this.storyboardDetails?.video?.campaign_id}&videoId=${this.videoId}`
      );
    }
  }

  toggleLeftPanel(): void {
    this.isLeftPanelCollapsed = !this.isLeftPanelCollapsed;
  }

  selectScene(sceneId: string): void {
    if (sceneId === "all") {
      // Handle "All Scenes" - combine all shots from all scenes
      const allShots: any[] = [];
      this.storyboardDetails?.shotlist?.scenes?.forEach((scene: any) => {
        if (scene.shots && scene.shots.length > 0) {
          allShots.push(...scene.shots);
        }
      });
      this.selectedScene = { id: "all", shots: allShots };
    } else {
      this.selectedScene = this.storyboardDetails?.shotlist?.scenes?.find(
        (scene) => scene.id === sceneId
      );
    }
    this.currentShotIndex = 0;
    this.updateSelectedFrameDetails();
  }

  getSetupNames(setups: any[]) {
    return setups.map((s) => s.setup_name).join(", ");
  }

  regenerate(shotId, operationType: "Edit" | "Create"): void {
    // Find the shot to get its description
    const shot = this.selectedScene?.shots?.find((x) => x.id === shotId);
    const shotDescription = shot?.description || "";

    // Open modal for user input
    const modalRef = this.modalService.create({
      nzTitle:
        operationType === "Edit"
          ? "Regenerate Storyboard Shot"
          : "Generate Storyboard Shot",
      nzFooter: null,
      nzWidth: 600,
      nzWrapClassName: "common-modal-design regenerate-shot-modal",
      nzCentered: true,
      nzContent: RegenerateStoryboardShotComponent,
    });

    const componentInstance =
      modalRef.getContentComponent() as RegenerateStoryboardShotComponent;
    componentInstance.shotId = shotId;
    componentInstance.shotDescription = shotDescription;

    componentInstance.onRegenerate.subscribe((modalData: any) => {
      if (modalData) {
        this.generateStoryboardShot(shotId, operationType, modalData);
      }
    });
  }

  generateStoryboardShot(
    shotId: string | number,
    operationType: "Edit" | "Create",
    modalData: { aiPrompt: string; imageStyle: string; aiModel: string }
  ): void {
    this.sharedService.showSpinner();

    this.storyboardService
      .onGenerateStoryBoardShotImage({
        shot_id: shotId,
        storyboard_id: this.storyboardId,
        operation: operationType,
        image_id: null,
        keymessage: modalData.aiPrompt,
        imagetype_id: modalData.imageStyle,
        model_id: modalData.aiModel,
      })
      .pipe(finalize(() => this.sharedService.hideSpinner()))
      .subscribe((res: any) => {
        if (res && res.statusCode === 200) {
          const ele = document.getElementById(
            "shot_images_" + shotId
          ) as HTMLImageElement;
          if (ele) {
            ele.src = res.data[0];
          }

          const shotInSelectedScene = this.selectedScene?.shots?.find(
            (x) => x.id === shotId
          );
          if (shotInSelectedScene) {
            shotInSelectedScene.image = res.data[0];
          }

          const scene = this.storyboardDetails?.shotlist?.scenes?.find(
            (scene) => scene.id === this.selectedScene?.id
          );
          if (scene) {
            const shotInScene = scene.shots?.find((x) => x.id === shotId);
            if (shotInScene) {
              shotInScene.image = res.data[0];
            }
          }

          this.sharedService.successToastNotification(
            operationType === "Edit"
              ? "Storyboard shot regenerated successfully"
              : "Storyboard shot generated successfully"
          );
        }
      });
  }

  switchView(mode: "grid" | "full"): void {
    this.viewMode = mode;
    if (mode === "full" && this.getCurrentShots().length > 0) {
      this.currentShotIndex = 0;
      this.updateSelectedFrameDetails();
    }
  }

  selectShot(shotId: number): void {
    const shots = this.getCurrentShots();
    const shotIndex = shots.findIndex((shot) => shot.id === shotId);
    if (shotIndex !== -1) {
      this.currentShotIndex = shotIndex;
      this.viewMode = "full";
      this.updateSelectedFrameDetails();
    }
  }

  navigateShot(direction: number): void {
    const shots = this.getCurrentShots();
    if (shots.length === 0) return;

    this.currentShotIndex += direction;
    if (this.currentShotIndex < 0) {
      this.currentShotIndex = shots.length - 1;
    } else if (this.currentShotIndex >= shots.length) {
      this.currentShotIndex = 0;
    }
    this.updateSelectedFrameDetails();
  }

  getCurrentShots(): any[] {
    return this.selectedScene?.shots || [];
  }

  getCurrentShot(): any {
    const shots = this.getCurrentShots();
    return shots[this.currentShotIndex] || null;
  }

  updateSelectedFrameDetails(): void {
    const currentShot = this.getCurrentShot();
    if (!currentShot) {
      this.selectedFrameDetails = null;
      return;
    }

    // Find the scene that contains this shot
    const scene = this.storyboardDetails?.shotlist?.scenes?.find((s: any) =>
      s.shots?.some((shot: any) => shot.id === currentShot.id)
    );

    this.selectedFrameDetails = {
      scene: scene ? `Scene ${scene.scene_number}: ${scene.scene_title}` : "",
      sceneDescription: scene?.scene_description || "",
      shotId: currentShot.id,
      shotDescription: currentShot.description || "",
      type: currentShot.shot_type || "",
      angle: currentShot.angle || "",
      lens: currentShot.lens || "",
      entities:
        currentShot.entities
          ?.map((e: any) => e?.entity?.name)
          .filter(Boolean) || [],
      script: currentShot.script || "",
      mood: currentShot.mood || "",
    };
  }

  switchTab(tab: "details" | "script" | "mood"): void {
    this.activeTab = tab;
  }

  editShot(shotId: number | string): void {
    // Find the shot in the selected scene or all scenes
    let shot: any = null;
    let sceneId: number | string = null;

    if (this.selectedScene?.id === "all") {
      // Search through all scenes to find the shot
      for (const scene of this.storyboardDetails?.shotlist?.scenes || []) {
        const foundShot = scene?.shots?.find((s: any) => s.id === shotId);
        if (foundShot) {
          shot = foundShot;
          sceneId = scene.id;
          break;
        }
      }
    } else {
      // Find shot in the selected scene
      shot = this.selectedScene?.shots?.find((s: any) => s.id === shotId);
      sceneId = this.selectedScene?.id;
    }

    if (shot && sceneId) {
      this.openShotModal(sceneId, shot);
    }
  }

  openShotModal(sceneId: number | string, shotData: any = null): void {
    // const modalRef = this.modalService.create({
    //   nzTitle: shotData ? "Edit Shot" : "Add Shot to Scene",
    //   nzFooter: null,
    //   nzWidth: 600,
    //   nzWrapClassName: "common-modal-design add-edit-shot-modal",
    //   nzCentered: true,
    //   nzContent: AddEditShotlistShotComponent,
    // });

    // const componentInstance =
    //   modalRef.getContentComponent() as AddEditShotlistShotComponent;
    // componentInstance.sceneId = sceneId;
    // componentInstance.shotData = shotData;

    // componentInstance.onShotSaved.subscribe((payload: any) => {
    //   if (payload) {
    //     // Refresh storyboard details after save
    //     this.fetchStoryboardDetails();
    //   }
    // });
  }

  triggerFileInput(shotId: number | string, event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    const fileInput = document.getElementById(
      "file-input-" + shotId
    ) as HTMLInputElement;
    if (fileInput) {
      // Use setTimeout to ensure the click event is fully processed
      setTimeout(() => {
        fileInput.click();
      }, 0);
    }
  }

  onFileSelected(event: Event, shotId: number | string): void {
    event.stopPropagation();
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) {
      return;
    }

    // Validate file size (5MB = 5 * 1024 * 1024 bytes)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      this.sharedService.errorToastNotification(
        "File size exceeds 5MB limit. Please select a smaller file."
      );
      input.value = ""; // Reset input
      return;
    }

    // Validate file type (images only)
    if (!file.type.startsWith("image/")) {
      this.sharedService.errorToastNotification(
        "Please select an image file only."
      );
      input.value = ""; // Reset input
      return;
    }

    // Create FormData
    const formData = new FormData();
    formData.append("shot_id", shotId.toString());
    formData.append("storyboard_id", this.storyboardId?.toString() || "");
    formData.append("file", file);

    // Upload file
    this.uploadStoryImage(formData, shotId);
  }

  uploadStoryImage(formData: FormData, shotId: number | string): void {
    this.sharedService.showSpinner();
    this.storyboardService
      .uploadStoryImage(formData)
      .pipe(finalize(() => this.sharedService.hideSpinner()))
      .subscribe(
        (res: any) => {
          if (res && res.statusCode === 200) {
            // Update the image in the UI
            const ele = document.getElementById(
              "shot_images_" + shotId
            ) as HTMLImageElement;
            if (ele && res.data[0]) {
              ele.src = res.data[0];
            }

            // Update shot in selected scene
            const shotInSelectedScene = this.selectedScene?.shots?.find(
              (x) => x.id === shotId
            );
            if (shotInSelectedScene) {
              if (!shotInSelectedScene.story_images) {
                shotInSelectedScene.story_images = [];
              }
              if (shotInSelectedScene.story_images.length === 0) {
                shotInSelectedScene.story_images.push({ image: res.data[0] });
              } else {
                shotInSelectedScene.story_images[0].image = res.data[0];
              }
            }

            // Update shot in storyboard details
            const scene = this.storyboardDetails?.shotlist?.scenes?.find(
              (scene) => scene.id === this.selectedScene?.id
            );
            if (scene) {
              const shotInScene = scene.shots?.find((x) => x.id === shotId);
              if (shotInScene) {
                if (!shotInScene.story_images) {
                  shotInScene.story_images = [];
                }
                if (shotInScene.story_images.length === 0) {
                  shotInScene.story_images.push({ image: res.data[0] });
                } else {
                  shotInScene.story_images[0].image = res.data[0];
                }
              }
            }

            this.sharedService.successToastNotification(
              res.message || "Image uploaded successfully"
            );
          }
        },
        (error: any) => {
          this.sharedService.errorToastNotification(
            error?.error?.message || "Failed to upload image"
          );
        }
      );
  }

  share(): void {
    // Implement share functionality
    console.log("Share clicked");
  }

  export(): void {
    // Implement export functionality
    console.log("Export clicked");
  }
}
